import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CancelOutlined,
  RefreshOutlined,
  FirstPageRounded,
  LastPageRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { supporterService } from "../services/supporterService";
import {
  STORAGE_KEYS,
  MESSAGES,
  textPrimary,
  textSecondary,
} from "../../../constants/consts";
import { useNavigate } from "react-router-dom";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 13;

export default function SupporterCancelled({ user }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);

  const username =
    user?.username ||
    localStorage.getItem(STORAGE_KEYS.USERNAME) ||
    sessionStorage.getItem(STORAGE_KEYS.USERNAME) ||
    "";

  /* ─────────────── Veri yükleme ─────────────── */
  const loadData = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await supporterService.getSupportsFilter({
        personel: username,
        durum: "cancel",
      });
      const userIds = [...new Set(data.map((d) => String(d.user_id)))];
      const cache = {};
      for (let i = 0; i < userIds.length; i += 50) {
        const chunk = userIds.slice(i, i + 50);
        const res = await Promise.all(
          chunk.map((id) => supporterService.getUserById(id)),
        );
        chunk.forEach((id, j) => (cache[id] = res[j]));
      }
      setRows(
        data.map((d) => ({
          id: d.id,
          title: d.title,
          sunucu: cache[String(d.user_id)]?.sunucu_kodu ?? "",
          contract:
            d.contract === "no"
              ? "Yok"
              : d.contract === "yes"
                ? "Var"
                : d.contract === "New"
                  ? "Yeni"
                  : "",
          username: cache[String(d.user_id)]?.username ?? "",
          supporter: d.supporter || "Atanmadı",
          note: d.note || "",
        })),
      );
      setPage(0);
    } catch (e) {
      console.error(e);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [username]);

  /* ─────────────── Sayfalama ─────────────── */
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  const pageRows = useMemo(() => {
    const start = safePage * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  const startIdx = safePage * PAGE_SIZE + 1;
  const endIdx = Math.min((safePage + 1) * PAGE_SIZE, rows.length);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* ─────────── BAŞLIK ─────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.20)",
          }}
        >
          <CancelOutlined sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={800}
            color="#fff"
            sx={{ fontSize: 16, lineHeight: 1.2 }}
          >
            {MESSAGES.SUPPORTER_CANCELLED_HEADER}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            İptal edilen destek talepleri listesi
          </Typography>
        </Box>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              px: 1.25,
              py: 0.4,
              borderRadius: 5,
              backgroundColor: "rgba(255,255,255,0.14)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 12,
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            {rows.length} kayıt
          </Box>
          <Tooltip title="Yenile">
            <span>
              <IconButton
                size="small"
                onClick={() => loadData(true)}
                disabled={loading || refreshing}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
                }}
              >
                <RefreshOutlined
                  sx={{
                    fontSize: 18,
                    animation:
                      loading || refreshing
                        ? "spin 1.2s linear infinite"
                        : "none",
                    "@keyframes spin": {
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <>
          {/* ─────────── TABLO ─────────── */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255,255,255,0.98)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
              border: "1px solid rgba(255,255,255,0.30)",
              animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15,50,65,0.20)",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(15,50,65,0.35)",
                },
                "&::-webkit-scrollbar-track": { background: "transparent" },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        // Gradient ile uyumlu yumuşak slate
                        background:
                          "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)",
                        color: "#334155",
                        fontWeight: 800,
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #CBD5E1",
                        whiteSpace: "nowrap",
                      },
                      "& th:first-of-type": {
                        borderTopLeftRadius: 10,
                        pl: 2,
                      },
                      "& th:last-of-type": {
                        borderTopRightRadius: 10,
                        pr: 2,
                      },
                    }}
                  >
                    <TableCell>Konu</TableCell>
                    <TableCell>Sunucu</TableCell>
                    <TableCell>Sözleşme</TableCell>
                    <TableCell>Hizmet Alan</TableCell>
                    <TableCell>Hizmet Veren</TableCell>
                    <TableCell>Not</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: textSecondary,
                          fontSize: 13,
                        }}
                      >
                        İptal edilmiş talep bulunmuyor
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r, i) => (
                      <TableRow
                        key={r.id}
                        onClick={() =>
                          navigate(`/support/${r.id}/messages`, {
                            state: {
                              title: r.title,
                              status: "cancel",
                              sunucuKodu: r.sunucu,
                            },
                          })
                        }
                        sx={{
                          cursor: "pointer",
                          backgroundColor: i % 2 ? "#F8FAFC" : "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(226,75,74,0.10)",
                          },
                          transition: "background-color 0.2s ease",
                          animation: `${fadeInUp} 0.35s ease-out ${
                            i * 0.03
                          }s backwards`,
                          "& td": {
                            py: 0.75,
                            px: 1.5,
                            fontSize: 12.5,
                            color: "#334155",
                            borderBottom: "1px solid #F1F5F9",
                          },
                          "& td:first-of-type": { pl: 2 },
                          "& td:last-of-type": { pr: 2 },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: textPrimary,
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.title}
                        >
                          {r.title}
                        </TableCell>
                        <TableCell>{r.sunucu}</TableCell>
                        <TableCell>
                          <ContractChip contract={r.contract} />
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.username}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.supporter}
                        </TableCell>
                        <TableCell>
                          {r.note ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                px: 0.9,
                                py: 0.2,
                                borderRadius: 1,
                                backgroundColor: "#FCEBEB",
                                color: "#791F1F",
                                fontWeight: 700,
                                fontSize: 11,
                                maxWidth: 180,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                              title={r.note}
                            >
                              {r.note}
                            </Box>
                          ) : (
                            <Box component="span" sx={{ color: "#94A3B8" }}>
                              —
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* ─────────── SAYFALAMA ─────────── */}
          {rows.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                px: 0.5,
                animation: `${fadeInUp} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {startIdx}–{endIdx} / {rows.length} kayıt
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PagerBtn
                  icon={FirstPageRounded}
                  disabled={safePage === 0}
                  onClick={() => setPage(0)}
                />
                <PagerBtn
                  icon={ChevronLeftRounded}
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                />

                <Box
                  sx={{
                    mx: 1,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    backgroundColor: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    minWidth: 62,
                    textAlign: "center",
                  }}
                >
                  {safePage + 1} / {totalPages}
                </Box>

                <PagerBtn
                  icon={ChevronRightRounded}
                  disabled={safePage >= totalPages - 1}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                />
                <PagerBtn
                  icon={LastPageRounded}
                  disabled={safePage >= totalPages - 1}
                  onClick={() => setPage(totalPages - 1)}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

/* ─────────────── Sayfalama butonu ─────────────── */
function PagerBtn({ icon: Icon, disabled, onClick }) {
  return (
    <IconButton
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 30,
        height: 30,
        color: "#fff",
        backgroundColor: "rgba(255,255,255,0.10)",
        border: "1px solid rgba(255,255,255,0.18)",
        transition:
          "transform 0.18s ease, background-color 0.18s ease, color 0.18s ease",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.22)",
          transform: "scale(1.06)",
        },
        "&:active": { transform: "scale(0.92)" },
        "&.Mui-disabled": {
          color: "rgba(255,255,255,0.25)",
          backgroundColor: "rgba(255,255,255,0.04)",
          borderColor: "rgba(255,255,255,0.08)",
        },
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </IconButton>
  );
}

/* ─────────────── Sözleşme rozeti ─────────────── */
function ContractChip({ contract }) {
  const map = {
    Var: { fg: "#15803D", bg: "#DCFCE7", border: "#86EFAC" },
    Yok: { fg: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5" },
    Yeni: { fg: "#1D4ED8", bg: "#DBEAFE", border: "#93C5FD" },
  };
  const theme = map[contract] || {
    fg: "#475569",
    bg: "#F1F5F9",
    border: "#CBD5E1",
  };
  if (!contract)
    return (
      <Box component="span" sx={{ color: "#94A3B8" }}>
        —
      </Box>
    );
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.9,
        py: 0.2,
        borderRadius: 1,
        backgroundColor: theme.bg,
        color: theme.fg,
        border: `1px solid ${theme.border}`,
        fontWeight: 700,
        fontSize: 10.5,
        verticalAlign: "middle",
      }}
    >
      {contract}
    </Box>
  );
}
